import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useAppStore } from '../store/appStore';
import './Auth.css';

export default function Login() {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const { setToken, setUser } = useAuthStore();
  const { setCompanies, setDevices, setUsers, setDepots, setNotifications, setAuditLogs } = useAppStore();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    try {
      // Clear all previous user data
      setCompanies([]);
      setDevices([]);
      setUsers([]);
      setDepots([]);
      setNotifications([]);
      setAuditLogs([]);
      
      const response = await authAPI.login(formData.username, formData.password);
      if (response.data.biometric_required) {
        setErrorMessage('Use the Android app to complete biometric sign-in.');
        return;
      }
      const { access_token } = response.data;
      
      // Set token first so the next request will have it
      setToken(access_token);
      
      // Fetch full user data including depot_id
      const userInfoResponse = await authAPI.getCurrentUser();
      const userData = userInfoResponse.data;
      
      // Check if user is driver - they cannot access the website
      if (userData.user_type === 'driver') {
        toast.error('Drivers can only access the mobile app. Please use your mobile device to login.');
        setToken(null); // Clear the token since we rejected this user
        setLoading(false);
        return;
      }
      
      // Check if user is verificador - they cannot access the website
      if (userData.user_type === 'verificador') {
        toast.error('Verificador users can only access the mobile app. Please use your mobile device to login.');
        setToken(null); // Clear the token since we rejected this user
        setLoading(false);
        return;
      }
      
      // Store full user object with all fields including depot_id
      setUser({
        id: userData.id,
        username: userData.username,
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        user_type: userData.user_type,
        company_id: userData.company_id,
        depot_id: userData.depot_id,
        is_active: userData.is_active,
      });
      
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      setToken(null);
      setUser(null);
      const detail = error.response?.data?.detail;
      setErrorMessage(typeof detail === 'string' ? detail : 'Unable to sign in. Check your credentials and try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h1>Sailog</h1>
        <h2>Login</h2>
        
        {errorMessage && <p className="error-message" role="alert">{errorMessage}</p>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username" autoComplete="username" name="username"
              value={formData.username}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password" autoComplete="current-password" name="password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <p className="auth-link">
          Forgot your password? <Link to="/reset-password">Reset it here</Link>
        </p>
      </div>
    </div>
  );
}
